import { Controller } from '@hotwired/stimulus';

export class LandingAnimationsController extends Controller<HTMLElement> {
    private observer?: IntersectionObserver;
    private readonly reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    connect(): void {
        const elements = this.animatedElements();
        this.startHeadlineAnimation();

        if (this.reducedMotion.matches || !('IntersectionObserver' in window)) {
            for (const element of elements) {
                element.classList.add('landing-reveal-visible');
            }
            return;
        }

        this.observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) {
                        continue;
                    }

                    entry.target.classList.add('landing-reveal-visible');
                    this.observer?.unobserve(entry.target);
                }
            },
            {
                root: null,
                rootMargin: '0px 0px -12% 0px',
                threshold: 0.12,
            },
        );

        elements.forEach((element, index) => {
            element.classList.add('landing-reveal-pending');
            element.style.setProperty('--landing-reveal-delay', `${Math.min(index % 6, 5) * 55}ms`);
            this.observer?.observe(element);
        });
    }

    disconnect(): void {
        this.observer?.disconnect();
        this.observer = undefined;
    }

    private startHeadlineAnimation(): void {
        if (this.reducedMotion.matches) {
            return;
        }

        const headline = this.element.querySelector<HTMLElement>('.landing-headline');
        if (!headline || headline.dataset.landingHeadlineAnimated === 'true') {
            return;
        }

        headline.dataset.landingHeadlineAnimated = 'true';
        requestAnimationFrame(() => {
            headline.classList.add('landing-headline-animate');
        });
    }

    private animatedElements(): HTMLElement[] {
        const selectors = [
            ':scope > section',
            'section article',
            'section figure',
            'section [class*="rounded-[2rem]"]',
            'section [class*="rounded-3xl"]',
        ];
        const seen = new Set<HTMLElement>();
        const elements: HTMLElement[] = [];

        for (const selector of selectors) {
            for (const element of this.element.querySelectorAll<HTMLElement>(selector)) {
                if (seen.has(element) || element.closest('[data-landing-animation-skip]')) {
                    continue;
                }

                seen.add(element);
                elements.push(element);
            }
        }

        return elements;
    }
}
